<template>
  <div>
    <el-table
      stripe
      :data="ptos"
      :default-sort="defaultSort"
      style="width: 100%"
      v-if="count > 0"
      :border="true"
      class="depth">

      <el-table-column
        prop="user.profile.first_name"
        v-if="!this.user_id && showColumns.name"
        label="Name" sortable>
        <template slot-scope="scope">
          <span v-if="scope.row.user.profile">
            {{ scope.row.user.profile.first_name }} {{ scope.row.user.profile.last_name }}
          </span>
          <span v-else>
            <i>[User's name is not set yet]</i>
          </span>
        </template>
      </el-table-column>

      <el-table-column
        label="Status"
        v-if="showColumns.status" align="center" sortable>
        <template slot-scope="scope">
          <el-tag size="small" :type="setType(scope.row)" effect="dark">{{ setValue(scope.row) }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column
        prop="category.name"
        label="Category"
        v-if="showColumns.category" align="center" sortable>
      </el-table-column>

      <el-table-column
        prop="starts_at"
        label="Start"
        v-if="showColumns.start" align="right" sortable>
        <template slot-scope="scope">
          {{ scope.row.starts_at | formatTime('MMMM DD, YYYY hh:mm a') }}
        </template>
      </el-table-column>

      <el-table-column
        prop="ends_at"
        label="End"
        v-if="showColumns.end" align="right" sortable>
        <template slot-scope="scope">
          {{ scope.row.ends_at | formatTime('MMMM DD, YYYY hh:mm a') }}
        </template>
      </el-table-column>

      <el-table-column
        prop="created_at"
        label="Requested"
        v-if="showColumns.requested" align="right" sortable>
        <template slot-scope="scope">
          {{ scope.row.created_at | formatTime('MMMM DD, YYYY hh:mm a') }}
        </template>
      </el-table-column>

      <el-table-column
        prop="requested_offdays"
        label="Duration"
        v-if="showColumns.duration" align="center" sortable>
        <template slot-scope="scope">
          {{ scope.row.requested_offdays }} day(s)
        </template>
      </el-table-column>

      <el-table-column
        label=""
        :width="actionsWidth"
        v-if="showColumns.actions" align="center">
        <template slot-scope="scope">
          <el-button
            v-if="canEditPto(scope.row)"
            type="warning"
            size="mini"
            @click="handleEdit(scope.row)">
            <i class="fas fa-edit"></i>
          </el-button>
          <router-link :to="{ name: 'pto', params: { id: scope.row.id } }"><el-button type="primary" size="mini"><i class="fas fa-search"></i></el-button></router-link>
          <el-button
            v-if="canDeletePto(scope.row)"
            type="danger"
            size="mini"
            :loading="deletingId === scope.row.id"
            @click="handleDelete(scope.row)">
            <i class="fas fa-trash-alt"></i>
          </el-button>
        </template>
      </el-table-column>

    </el-table>

    <div v-else>
      <el-alert type="info" :closable="false" title="There are no PTO entries!" center></el-alert>
    </div>

  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import CalendarService from '@/services/calendarService';
  import { showSuccessMessage, showErrorMessage } from '@/ui/message';

  export default {
    name: 'PtosList',
    props: {
      ptos: {
        type: Array,
        required: true,
      },
      user_id: {
        type: Number,
      },
      showColumns: {
        type: Object,
        required: false,
        default: () => ({ status: true, name: true, category: true, start: true, end: true, requested: true, duration: true, actions: true }),
      },
      defaultSort: {
        type: Object,
        required: false,
        default: () => ({ prop: 'created_at', order: 'descending' }),
      },
      showPendingEdit: {
        type: Boolean,
        required: false,
        default: false,
      },
      onEditPto: {
        type: Function,
        required: false,
        default: null,
      },
    },
    data() {
      return {
        deletingId: null,
      };
    },
    computed: {
      ...mapGetters('entities/users', ['currentUser']),
      count() {
        return this.ptos ? this.ptos.length : 0;
      },
      isAdmin() {
        return !!(this.currentUser && this.currentUser.permissions && this.currentUser.permissions.includes('admin'));
      },
      canDeleteAny() {
        if (!this.currentUser) return false;
        if (this.isAdmin) return true;
        return (this.ptos || []).some(pto => pto.user_id === this.currentUser.id && pto.status === 'pending');
      },
      actionsWidth() {
        let width = 100;
        if (this.showPendingEdit) width += 50;
        if (this.canDeleteAny) width += 50;
        return width;
      },
    },
    methods: {
      ...mapActions('entities/ptos', ['deletePto']),

      canEditPto(pto) {
        return this.showPendingEdit
          && !!this.currentUser
          && pto
          && pto.status === 'pending'
          && pto.user_id === this.currentUser.id;
      },
      handleEdit(pto) {
        if (this.onEditPto && this.canEditPto(pto)) {
          this.onEditPto(pto);
        }
      },
      canDeletePto(pto) {
        if (!this.currentUser || !pto) return false;
        const isOwnPending = pto.user_id === this.currentUser.id && pto.status === 'pending';
        return isOwnPending || this.isAdmin;
      },
      getErrorMessage(error, fallback = 'Unable to delete PTO request.') {
        if (!error) return fallback;
        if (typeof error === 'string') return error;
        if (Array.isArray(error)) return error.join(', ');
        if (error.message) return error.message;
        return fallback;
      },
      handleDelete(pto) {
        this.$confirm('Do you really want to delete this PTO request? This action cannot be undone.', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          this.deletingId = pto.id;
          this.deletePto(pto.id)
            .then((response) => {
              if (response.status) {
                showSuccessMessage('PTO request has been deleted.');
                this.$emit('pto-deleted', pto.id);
              } else {
                showErrorMessage(this.getErrorMessage(response.error));
              }
            })
            .finally(() => {
              this.deletingId = null;
            });
        }).catch(() => {});
      },
      getDays(date1, date2) {
        return CalendarService.getDays(date1, date2);
      },
      getHours(date1, date2) {
        return CalendarService.getHours(date1, date2);
      },
      setType(pto) {
        if (pto) {
          switch (pto.status) {
            case 'approved':
              return 'success';
            case 'rejected':
              return 'danger';
            case 'pending':
              return 'warning';
            default:
              return 'info';
          }
        }
        return 'info';
      },
      setValue(pto) {
        if (pto) {
          switch (pto.status) {
            case 'approved':
              return 'Approved';
            case 'rejected':
              return 'Rejected';
            case 'pending':
              return 'Pending';
            default:
              return 'Not Submitted';
          }
        }
        return 'Not Submitted';
      },
    },
  };
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/components/pto/PtosList.vue